import type { ReactNode, SVGProps } from "react";
import { LottieCamera } from "./LottieCamera.js";

/**
 * Self-authored inline-SVG icon set (no icon-font, no dependency) so glyphs render IDENTICALLY on every
 * OS/browser — Unicode/emoji glyphs (⌘ ✓ 文 🔔 🇯🇵 …) otherwise fall back to different shapes / emoji,
 * especially on iOS. Line icons: 24×24 viewBox, stroke = currentColor (the nav lime-active / muted /
 * danger states just work). Pass `className="ic-spin"` to animate (e.g. busy/processing).
 */
export type IconName =
  | "dashboard"
  | "capture"
  | "home"
  | "library"
  | "inbox"
  | "approval"
  | "ai-review"
  | "members"
  | "settings"
  | "analytics"
  | "learning"
  | "multilingual"
  | "studio"
  | "versions"
  | "feedback"
  | "ops"
  | "billing"
  | "more"
  | "chevron"
  | "power"
  | "menu"
  | "close"
  // added in the app-wide migration
  | "arrow-right"
  | "arrow-left"
  | "search"
  | "plus"
  | "upload"
  | "print"
  | "warning"
  | "info"
  | "offline"
  | "play"
  | "pause"
  | "mic"
  | "speaker"
  | "edit"
  | "pen"
  | "frame"
  | "eraser"
  | "backspace"
  | "undo"
  | "redo-cycle"
  | "reorder"
  | "table"
  | "target"
  | "star"
  | "image-off"
  | "spinner"
  | "check"
  | "dot-filled"
  | "dot-empty"
  | "qr"
  | "login"
  | "onboarding"
  | "storage"
  | "audit"
  | "brand-kit"
  | "flag"
  | "industry-food"
  | "industry-factory"
  | "industry-medical"
  | "industry-hotel"
  | "empty-box"
  // the three "create a manual from …" mode symbols (Dashboard/Home cards)
  | "mode-video"
  | "mode-image"
  | "mode-screen";

const PATHS: Record<IconName, ReactNode> = {
  dashboard: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.2" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.2" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.2" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.2" />
    </>
  ),
  capture: (
    <>
      <rect x="3" y="6.5" width="18" height="13" rx="2.5" />
      <path d="M8.2 6.5 9.6 4h4.8l1.4 2.5" />
      <circle cx="12" cy="13" r="3.2" />
    </>
  ),
  home: (
    <>
      <path d="M4 11.5 12 4l8 7.5" />
      <path d="M6 10.5V20h12v-9.5" />
    </>
  ),
  library: (
    <>
      <rect x="4" y="4.5" width="16" height="15" rx="2" />
      <path d="M9 4.5v15" />
      <path d="M12.5 9h4M12.5 12.5h4" />
    </>
  ),
  inbox: (
    <>
      <path d="M4 13 6.4 5.5h11.2L20 13v5.5H4z" />
      <path d="M4 13h4l1.4 2.4h5L16 13h4" />
    </>
  ),
  approval: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.4 12.2l2.4 2.4 4.8-5.2" />
    </>
  ),
  "ai-review": (
    <>
      <path d="M11 3.2l1.7 4.4 4.4 1.7-4.4 1.7L11 15.4l-1.7-4.4L4.9 9.3l4.4-1.7z" />
      <path d="M18 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" fill="currentColor" stroke="none" />
    </>
  ),
  members: (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.6 19c0-3 2.4-5 5.4-5s5.4 2 5.4 5" />
      <path d="M15.8 6.3a3 3 0 0 1 0 5.4" />
      <path d="M16.4 14.2c2.2.5 3.9 2.3 3.9 4.8" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.6v3M12 18.4v3M21.4 12h-3M5.6 12h-3M18.6 5.4l-2.1 2.1M7.5 16.5l-2.1 2.1M18.6 18.6l-2.1-2.1M7.5 7.5 5.4 5.4" />
    </>
  ),
  analytics: (
    <>
      <path d="M4 20h16" />
      <path d="M7 17v-6M12 17V6M17 17v-4" />
    </>
  ),
  learning: (
    <>
      <path d="M12 4.8 2.8 9 12 13.2 21.2 9z" />
      <path d="M6.5 11v4.2c0 1.4 2.5 2.5 5.5 2.5s5.5-1.1 5.5-2.5V11" />
      <path d="M21.2 9.2v5" />
    </>
  ),
  multilingual: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.6 12h16.8" />
      <path d="M12 3.5c2.4 2.4 2.4 14.6 0 17M12 3.5c-2.4 2.4-2.4 14.6 0 17" />
    </>
  ),
  studio: (
    <>
      <rect x="4" y="4.5" width="16" height="15" rx="2" />
      <path d="M4 9.2h16M9 9.2v10.3" />
    </>
  ),
  versions: (
    <>
      <path d="M12 3.4 3.4 8 12 12.6 20.6 8z" />
      <path d="M3.4 12 12 16.6 20.6 12" />
      <path d="M3.4 16 12 20.6 20.6 16" />
    </>
  ),
  feedback: (
    <>
      <path d="M5 5.5h14a1.6 1.6 0 0 1 1.6 1.6V15a1.6 1.6 0 0 1-1.6 1.6h-8.4L6 20.2V16.6H5A1.6 1.6 0 0 1 3.4 15V7.1A1.6 1.6 0 0 1 5 5.5z" />
      <path d="M8 9.6h8M8 12.6h5" />
    </>
  ),
  ops: (
    <>
      <path d="M4 15a8 8 0 0 1 16 0" />
      <path d="M12 15l3.6-3.2" />
      <circle cx="12" cy="15" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  billing: (
    <>
      <rect x="3.4" y="5.5" width="17.2" height="13" rx="2" />
      <path d="M3.4 9.6h17.2M7 14.6h4" />
    </>
  ),
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  chevron: <path d="M6 9.5l6 6 6-6" />,
  power: (
    <>
      <path d="M12 3.4v8" />
      <path d="M7.6 6.6a8 8 0 1 0 8.8 0" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  "arrow-right": (
    <>
      <path d="M4 12h15" />
      <path d="M13 6l6 6-6 6" />
    </>
  ),
  "arrow-left": (
    <>
      <path d="M20 12H5" />
      <path d="M11 6l-6 6 6 6" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-4.2-4.2" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  upload: (
    <>
      <path d="M12 15.5V4.5" />
      <path d="M7.5 9 12 4.5 16.5 9" />
      <path d="M5 14.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3.5" />
    </>
  ),
  print: (
    <>
      <path d="M7 8.5V3.8h10v4.7" />
      <path d="M7 16.5H5.4A1.4 1.4 0 0 1 4 15.1V11a1.4 1.4 0 0 1 1.4-1.4h13.2A1.4 1.4 0 0 1 20 11v4.1a1.4 1.4 0 0 1-1.4 1.4H17" />
      <rect x="7" y="14.3" width="10" height="6" rx="1" />
      <circle cx="16.6" cy="12" r=".7" fill="currentColor" stroke="none" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4 21 19.5H3z" />
      <path d="M12 10v4.4" />
      <circle cx="12" cy="17.2" r=".7" fill="currentColor" stroke="none" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.4" />
      <circle cx="12" cy="7.8" r=".7" fill="currentColor" stroke="none" />
    </>
  ),
  offline: (
    <>
      <path d="M5.5 12.5a9 9 0 0 1 13 0" />
      <path d="M8.3 15.4a5 5 0 0 1 7.4 0" />
      <circle cx="12" cy="18.4" r="1" fill="currentColor" stroke="none" />
      <path d="M3.5 3.5l17 17" />
    </>
  ),
  play: <path d="M8 5.5l11 6.5-11 6.5z" fill="currentColor" stroke="none" />,
  pause: (
    <>
      <rect x="7" y="5.5" width="3.3" height="13" rx="1" fill="currentColor" stroke="none" />
      <rect x="13.7" y="5.5" width="3.3" height="13" rx="1" fill="currentColor" stroke="none" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3.5" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
      <path d="M12 18v2.5M9 20.5h6" />
    </>
  ),
  speaker: (
    <>
      <path d="M4 9.5h3.3L12 6v12l-4.7-3.5H4z" />
      <path d="M15.4 9.6a3.5 3.5 0 0 1 0 4.8M17.7 7.6a6.5 6.5 0 0 1 0 8.8" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4l10-10-4-4L4 16z" />
      <path d="M13.5 6.5l4 4" />
    </>
  ),
  pen: (
    <>
      <path d="M9 14l-2.2 5.2 5.2-2.2 8-8a2.1 2.1 0 0 0-3-3z" />
      <path d="M5 21h6.5" />
      <path d="M14 7l3 3" />
    </>
  ),
  frame: (
    <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
  ),
  eraser: (
    <>
      <path d="M8.5 18 4.6 14.1a1.6 1.6 0 0 1 0-2.3l7-7a1.6 1.6 0 0 1 2.3 0l4.4 4.4a1.6 1.6 0 0 1 0 2.3L13.5 18z" />
      <path d="M8.5 18H20" />
      <path d="M8.2 8.4l5.6 5.6" />
    </>
  ),
  backspace: (
    <>
      <path d="M9 5.5h9A1.5 1.5 0 0 1 19.5 7v10A1.5 1.5 0 0 1 18 18.5H9L3.5 12z" />
      <path d="M11.5 9.5l5 5M16.5 9.5l-5 5" />
    </>
  ),
  undo: (
    <>
      <path d="M9 7 4.5 11.5 9 16" />
      <path d="M4.5 11.5H14a5.5 5.5 0 0 1 0 11h-2.5" />
    </>
  ),
  "redo-cycle": (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.3-5.4" />
      <path d="M19.5 4.5V9.5H14.5" />
    </>
  ),
  reorder: (
    <>
      <path d="M7 4.5v15M7 4.5 4.5 7M7 4.5 9.5 7" />
      <path d="M17 19.5v-15M17 19.5 14.5 17M17 19.5 19.5 17" />
    </>
  ),
  table: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="1.5" />
      <path d="M4 9.5h16M10 9.5V19M4 14.5h16" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1.5v3M12 19.5v3M22.5 12h-3M4.5 12h-3" />
    </>
  ),
  star: (
    <path d="M12 4l2.3 5 5.4.5-4.1 3.6 1.2 5.3L12 20.6 7.2 18.4 8.4 13.1 4.3 9.5l5.4-.5z" />
  ),
  "image-off": (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.4" />
      <path d="M4.5 17l5-4.5 3.5 3" />
      <path d="M3.5 3.5l17 17" />
    </>
  ),
  spinner: <path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5" />,
  check: <path d="M5 12.5l4.5 4.5L19 7" />,
  "dot-filled": <circle cx="12" cy="12" r="5" fill="currentColor" stroke="none" />,
  "dot-empty": <circle cx="12" cy="12" r="5" />,
  qr: (
    <>
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <path d="M14 14h2.5v2.5M20 14.5v5.5M14.5 20h2" />
    </>
  ),
  login: (
    <>
      <path d="M14 4.5h3.5A1.5 1.5 0 0 1 19 6v12a1.5 1.5 0 0 1-1.5 1.5H14" />
      <path d="M4 12h10" />
      <path d="M10.5 8.5 14 12l-3.5 3.5" />
    </>
  ),
  onboarding: (
    <>
      <path d="M5 19l8.5-8.5" />
      <path d="M13.8 4.4l1 2.5 2.5 1-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1z" />
      <path d="M5 19l-.6 1.6L6 20z" fill="currentColor" stroke="none" />
    </>
  ),
  storage: (
    <>
      <ellipse cx="12" cy="6" rx="7" ry="2.6" />
      <path d="M5 6v6c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V6" />
      <path d="M5 12v6c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-6" />
    </>
  ),
  audit: (
    <>
      <rect x="5.5" y="4.5" width="13" height="16" rx="1.5" />
      <path d="M9 4.5V3.6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v.9" />
      <path d="M8.5 12l2 2 4-4.5" />
    </>
  ),
  "brand-kit": (
    <>
      <path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.2 0 1.7-1 1.4-2-.4-1.3.6-2.3 1.9-2.3H18a3 3 0 0 0 3-3 8.5 8.5 0 0 0-9-6.4z" />
      <circle cx="8" cy="11" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
      <circle cx="16" cy="10.5" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  flag: (
    <>
      <path d="M6 21V4" />
      <path d="M6 5h11l-2.4 3.4L17 12H6" />
    </>
  ),
  "industry-food": (
    <>
      <path d="M4 11h16a8 8 0 0 1-16 0z" />
      <path d="M3 11h18" />
      <path d="M9 7.5c0-1 1-1.4 1-2.5M12.5 7.5c0-1 1-1.4 1-2.5" />
    </>
  ),
  "industry-factory": (
    <>
      <path d="M4 20V11l5 3.5V11l5 3.5V7l6 4.5V20z" />
      <path d="M4 20h16" />
    </>
  ),
  "industry-medical": (
    <>
      <rect x="4" y="4.5" width="16" height="15" rx="2" />
      <path d="M12 8.5v7M8.5 12h7" />
    </>
  ),
  "industry-hotel": (
    <>
      <rect x="5" y="4.5" width="14" height="15" rx="1.5" />
      <path d="M9 8h2M13 8h2M9 11.5h2M13 11.5h2M10.5 19.5v-4h3v4" />
    </>
  ),
  "empty-box": <rect x="4.5" y="4.5" width="15" height="15" rx="2" strokeDasharray="3 3" />,
  // 動画から作成 — the user's camera line drawing (camera.svg), recolored to currentColor.
  // Uses its own viewBox "0 0 400 400" + stroke (see CUSTOM_VIEWBOX / CUSTOM_STROKE below).
  "mode-video": (
    <g transform="matrix(1.615,0,0,1.615,181.831,380.781)">
      <path d="M1.35,-166.465L11.25,-179.665L77.332,-179.665L87.15,-166.465" />
      <path d="M-67.95,-166.465L-67.95,-176.365L-41.55,-176.365L-41.55,-166.465" />
      <path d="M44.291,-153.1C73.474,-153.1,97.132,-129.461,97.132,-100.3C97.132,-71.139,73.474,-47.5,44.291,-47.5C15.108,-47.5,-8.55,-71.139,-8.55,-100.3C-8.55,-129.461,15.108,-153.1,44.291,-153.1Z" />
      <path d="M44.291,-138.509C65.41,-138.509,82.53,-121.402,82.53,-100.3C82.53,-79.198,65.41,-62.091,44.291,-62.091C23.172,-62.091,6.053,-79.198,6.053,-100.3C6.053,-121.402,23.172,-138.509,44.291,-138.509Z" />
      <path d="M-73.617,-143.293C-72.857,-144.382,-71.99,-145.42,-71.018,-146.392C-62.061,-155.349,-47.518,-155.326,-38.533,-146.341C-37.59,-145.398,-36.745,-144.393,-35.999,-143.34" />
      <path d="M-54.75,-143.365C-47.46,-143.365,-41.55,-137.437,-41.55,-130.124C-41.55,-122.811,-47.46,-116.882,-54.75,-116.882C-62.04,-116.882,-67.95,-122.811,-67.95,-130.124C-67.95,-137.437,-62.04,-143.365,-54.75,-143.365Z" />
      <path transform="matrix(1,0,0,1,1.25,0)" d="M108.5,-166.5C68.5,-166.5,-13,-206.5,-13,-246.5C-13,-326.5,67,-306.5,67,-366.5C67,-446.5,-13,-406.5,-33,-506.5" />
      <path d="M110.25,-166.465L-87.75,-166.465L-87.75,-34.3L110.25,-34.3L110.25,-166.465Z" />
      <path d="M-67.95,-87.182L-28.268,-87.182" />
      <path d="M-67.95,-74.065L-18.45,-74.065" />
      <path d="M-67.95,-60.782L-8.55,-60.782" />
    </g>
  ),
  // 画像から作成 — offset stack of photo frames (many stills → steps)
  "mode-image": (
    <>
      <path d="M8.5 5.4h8.1A1.5 1.5 0 0 1 18 6.9v8.1" />
      <path d="M6 7.9h8.1A1.5 1.5 0 0 1 15.5 9.4v8.1" />
      <rect x="3" y="10.4" width="10.5" height="9.6" rx="1.5" />
      <path d="M3.6 18.2l2.6-2.8 1.8 1.7 1.9-2.2 2.5 2.9" />
      <circle cx="6.1" cy="13.2" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  // 画面録画から作成 — monitor with a REC dot + descending ledger lines (screen → ordered steps)
  "mode-screen": (
    <>
      <rect x="2.8" y="4.5" width="18.4" height="12.5" rx="2.2" />
      <path d="M8 21h8M12 17v4" />
      <circle cx="6.5" cy="8" r="1.35" fill="currentColor" stroke="none" />
      <path d="M10.5 8h6.5M10.5 11h6.5M10.5 14h3.6" />
    </>
  ),
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 18, ...rest }: IconProps) {
  // mode-video is the user's Lottie camera (played as-is, recoloured); the rest are 24×24 line glyphs.
  if (name === "mode-video") return <LottieCamera size={size} style={rest.style} className={rest.className} />;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
